import {
  Globe,
  Users,
  Target,
  Swords,
  Handshake,
  ShieldCheck,
  TrendingUp,
  Rocket,
} from "lucide-react";
import { Reveal, SectionHead, Eyebrow } from "../components/Reveal";

const DOMAINS = [
  { icon: Globe, tag: "Market", desc: "States, cities, industries, demand, and trends." },
  { icon: Users, tag: "Buyers", desc: "Companies, decision-makers, buying centres, and pain points." },
  { icon: Target, tag: "Accounts", desc: "Target companies, priority scores, and buying triggers." },
  { icon: Swords, tag: "Competitors", desc: "Players, positioning, pricing, strengths, and weaknesses." },
  { icon: Handshake, tag: "Partners", desc: "Distributors, integrators, resellers, and strategic partners." },
  { icon: ShieldCheck, tag: "Regulation", desc: "Standards, certifications, import, tax, and compliance." },
  { icon: TrendingUp, tag: "Commercial execution", desc: "Pricing, economics, procurement, and margins." },
  { icon: Rocket, tag: "Activation", desc: "Entry model, sales playbook, and 90-day roadmap." },
];

const FRAMEWORKS = [
  ["ENEXUR India Opportunity Index™", "Measures the attractiveness of the Indian opportunity across market, demand, competition, accessibility, and strategic fit."],
  ["ENEXUR Account Priority Score™", "Identifies which companies should be pursued first based on fit, triggers, accessibility, and commercial potential."],
  ["ENEXUR Partner DNA™", "Evaluates partner capability, customer reach, technical depth, strategic fit, and competitive conflict."],
  ["ENEXUR India Entry Risk Score™", "Identifies potential failure points before capital and management attention are committed."],
  ["ENEXUR Market Validation Score™", "Measures whether enough market evidence exists to justify the next level of investment."],
  ["ENEXUR India Market Readiness Score™", "Measures whether the company, product, commercial model, and operating capabilities are ready for India."],
];

const DELIVERABLES = [
  "Executive Decision Brief",
  "India Opportunity Intelligence",
  "Industry and Segment Intelligence",
  "Geographic Opportunity Map",
  "Customer and Buyer Intelligence",
  "Top 100 Target Accounts",
  "Decision-Maker Intelligence",
  "Competitive Intelligence",
  "Partner and Distributor Intelligence",
  "Regulatory and Market-Access Intelligence",
  "Commercial and Pricing Intelligence",
  "90-Day India Entry Blueprint",
];

const ADDITIONAL = [
  "Primary market research",
  "Executive strategy session",
  "Intelligence Vault™",
  "Red Team India™",
  "First 10 Meetings Pack",
  "Post-delivery intelligence review",
];

export const ArchitectureSection = () => (
  <section id="architecture" className="py-24 md:py-32" data-testid="section-architecture">
    <div className="wrap">
      <SectionHead
        index="04 — Intelligence architecture"
        title={<>Eight domains. One evidence base.</>}
        lead="Every engagement draws these domains into a single, connected picture of the market."
      />
      <Reveal className="mt-16 md:mt-20">
        <div className="grid gap-px border border-gold/15 bg-gold/15 sm:grid-cols-2 lg:grid-cols-4">
          {DOMAINS.map((d) => (
            <div
              key={d.tag}
              className="group bg-ink p-8 transition-colors duration-300 hover:bg-abyss"
              data-testid={`architecture-${d.tag.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <d.icon size={20} strokeWidth={1.4} className="text-gold" aria-hidden="true" />
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-ivory/85">
                {d.tag}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-mist">{d.desc}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const FrameworksSection = () => (
  <section id="frameworks" className="py-24 md:py-32" data-testid="section-frameworks">
    <div className="wrap">
      <SectionHead
        index="05 — Proprietary frameworks"
        title={<>Named instruments for a named decision.</>}
      />
      <div className="mt-16 grid gap-6 md:mt-20 md:grid-cols-2 lg:grid-cols-3">
        {FRAMEWORKS.map(([name, desc], i) => (
          <Reveal key={name} delay={(i % 3) * 0.1}>
            <div className="flex h-full flex-col border border-gold/15 bg-abyss/50 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.28em] text-gold/70">
                  Instrument {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex gap-1" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((t) => (
                    <span
                      key={t}
                      className={`h-px w-3.5 ${t <= (i % 5) ? "bg-gold/70" : "bg-gold/20"}`}
                    />
                  ))}
                </span>
              </div>
              <h3 className="mt-7 font-display text-xl leading-snug text-ivory">{name}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-mist">{desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-10 max-w-2xl text-sm italic leading-relaxed text-mist/70">
          Scores and assessments are evidence-backed and traceable, where applicable
          to the engagement.
        </p>
      </Reveal>
    </div>
  </section>
);

export const DeliverablesSection = () => (
  <section id="deliverables" className="py-24 md:py-32" data-testid="section-deliverables">
    <div className="wrap">
      <SectionHead
        index="06 — What the client receives"
        title={<>The flagship deliverables.</>}
      />
      <Reveal className="mt-16 md:mt-20">
        <div className="grid gap-px border border-gold/15 bg-gold/15 sm:grid-cols-2 lg:grid-cols-3">
          {DELIVERABLES.map((d, i) => (
            <div
              key={d}
              className="group flex items-start gap-5 bg-ink p-7 transition-colors duration-300 hover:bg-abyss"
              data-testid={`deliverable-${i + 1}`}
            >
              <span className="font-mono text-sm text-gold">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display text-lg leading-snug text-ivory/90 transition-colors duration-300 group-hover:text-ivory">
                {d}
              </span>
            </div>
          ))}
        </div>
      </Reveal>

      <div className="mt-16">
        <Reveal>
          <Eyebrow>Additional components — where the engagement calls for them</Eyebrow>
          <div className="mt-8 flex flex-wrap gap-3">
            {ADDITIONAL.map((a) => (
              <span
                key={a}
                className="border border-gold/20 bg-abyss/60 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/85 transition-colors duration-200 hover:border-gold/50 hover:text-gold"
              >
                {a}
              </span>
            ))}
          </div>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist/60">
            Each component is scoped to the engagement.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
